/**
 * Documents API — the ingestion port at /api/finance/documents.
 *
 * Upload uses XMLHttpRequest rather than fetch because fetch still has no
 * upload-progress events; the DropZone needs per-file progress. Auth +
 * timezone headers come from the shared authHeader() so behavior matches
 * apiUpload exactly (401s here surface as ApiError and the caller retries
 * by re-dropping — an upload is not silently retried with a stale token).
 */

import {
  API_BASE,
  ApiError,
  apiClient,
  authHeader,
  buildQueryString,
  type PaginatedResponse,
} from "@/shared/lib/api/client";
import type {
  DocumentKind,
  DocumentUploadResponse,
  FinanceDocument,
} from "../types";

const BASE = "/api/finance/documents";

export interface UploadDocumentOptions {
  /** Optional pre-classified kind; the server classifier still verifies. */
  docKind?: DocumentKind;
  /** Optional account this document belongs to (bare key). */
  accountId?: string;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export function uploadDocument(
  file: File,
  options: UploadDocumentOptions = {},
): Promise<DocumentUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  if (options.docKind) form.append("doc_kind", options.docKind);
  if (options.accountId) form.append("account_id", options.accountId);

  return new Promise<DocumentUploadResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}${BASE}`);
    xhr.withCredentials = true;
    for (const [key, value] of Object.entries(authHeader())) {
      xhr.setRequestHeader(key, value);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && options.onProgress) {
        options.onProgress(event.loaded / event.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as DocumentUploadResponse);
        } catch {
          reject(
            new ApiError(xhr.status, xhr.statusText, "Malformed response"),
          );
        }
      } else {
        reject(new ApiError(xhr.status, xhr.statusText, xhr.responseText));
      }
    };
    xhr.onerror = () =>
      reject(new ApiError(0, "NetworkError", "Upload failed — network error"));
    xhr.onabort = () =>
      reject(new ApiError(0, "Aborted", "Upload cancelled"));

    if (options.signal) {
      if (options.signal.aborted) {
        xhr.abort();
        return;
      }
      options.signal.addEventListener("abort", () => xhr.abort(), {
        once: true,
      });
    }

    xhr.send(form);
  });
}

export function getDocument(documentId: string): Promise<FinanceDocument> {
  return apiClient<FinanceDocument>(`${BASE}/${documentId}`);
}

/**
 * The imports grid, newest first. The route takes skip/limit only
 * (router.py:414-419) — there is no server-side status filter, so callers
 * that want one filter the returned page.
 */
export function listDocuments(params?: {
  skip?: number;
  limit?: number;
}): Promise<PaginatedResponse<FinanceDocument>> {
  const qs = buildQueryString({
    skip: params?.skip ?? 0,
    limit: params?.limit ?? 50,
  });
  return apiClient<PaginatedResponse<FinanceDocument>>(`${BASE}${qs}`);
}

/** Re-run extraction (e.g. after a model fix) on a failed/completed doc. */
export function retryDocument(documentId: string): Promise<FinanceDocument> {
  return apiClient<FinanceDocument>(`${BASE}/${documentId}/retry`, {
    method: "POST",
  });
}
